import axios, { AxiosRequestConfig } from 'axios';

import * as URL from './url_helper';

const allowedPaths = ['/', '/login'];
const isPublicApi = (url?: string) => Boolean(url && /^\/?api\/public(\/|$)/.test(url));

type ApiRequestConfig = AxiosRequestConfig;

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  timeout: 5000,
  headers: {
    'Content-Type': 'application/json',
  },
});

axiosInstance.interceptors.request.use(
  async (config) => {
    if (config.url === URL.LOGIN || isPublicApi(config.url)) {
      if (config.headers?.Authorization) {
        delete config.headers.Authorization;
      }
      return config;
    }

    const token = localStorage.getItem('token');
    config.headers.Authorization = `Bearer ${token}`;

    if (config.url) {
      return config;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

axiosInstance.interceptors.response.use(
  function (response) {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const currentPath = window.location.pathname;

    if (currentPath === '/login') {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          const currentUrl = window.location.pathname + window.location.search;
          localStorage.setItem('redirectAfterLogin', currentUrl);
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          window.location.href = '/login';
          return Promise.reject(error);
        }
        const res = await axios.post(`${process.env.REACT_APP_BASE_URL}auth/refresh`, {
          refreshToken,
        });

        const newAccessToken = res.data.data.accessToken;
        const newRefreshToken = res.data.data.refreshToken;
        localStorage.setItem('token', newAccessToken);
        localStorage.setItem('refreshToken', newRefreshToken);

        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError: any) {
        if (
          refreshError.response &&
          refreshError.response.status === 401
        ) {
          const currentUrl = window.location.pathname + window.location.search;
          localStorage.setItem('redirectAfterLogin', currentUrl);
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  },
);

const apiClient = {
  get: (url: string, params?: Record<string, any>, config: ApiRequestConfig = {}) =>
    axiosInstance
      .get(url, { params, ...config })
      .then((res) => res)
      .catch((error) => {
        if (
          !allowedPaths.includes(window.location.pathname) &&
          error.response &&
          error.response.status === 401
        ) {
          const currentUrl = window.location.pathname + window.location.search;
          localStorage.setItem('redirectAfterLogin', currentUrl);
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          window.location.href = '/login';
        }
        throw error;
      }),

  post: (url: string, data?: any, config: ApiRequestConfig = {}) =>
    axiosInstance
      .post(url, data, config)
      .then((res) => res)
      .catch((error) => {
        if (
          !allowedPaths.includes(window.location.pathname) &&
          error.response &&
          error.response.status === 401
        ) {
          const currentUrl = window.location.pathname + window.location.search;
          localStorage.setItem('redirectAfterLogin', currentUrl);
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          window.location.href = '/login';
        }
        throw error;
      }),

  put: (url: string, data?: any, config: ApiRequestConfig = {}) =>
    axiosInstance.put(url, data, config),

  patch: (url: string, data?: any, config: ApiRequestConfig = {}) =>
    axiosInstance.patch(url, data, config),

  delete: (url: string, config: ApiRequestConfig = {}) => axiosInstance.delete(url, config),
};

export default apiClient;
